import { c as _c } from "react/compiler-runtime";
import { Price } from '@7f-ui/components/price';
import { TextExpander } from '@7f-ui/components/text-expander/text-expander';
import type { AccommodationRate } from '@data-access/graphql/schema.types';
import { Radio } from '@headlessui/react';
import { Circle } from '@phosphor-icons/react';
import { formatGermanNumber } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import type { Dispatch, SetStateAction } from 'react';
import { HOTEL_ADS_NAV_ORDER, hotelAdsCriticalPath } from '@/lib/hotel-ads/nav';
import { AccommodationDiscount } from '../../accommodation-discount';
import { AccommodationFlag } from '../../accommodation-flag';
import { CancelationPolicy } from './cancelation-policy';
import { LineItem } from './line-item';
import { MealPlan } from './meal-plan';
export const RATES_WIDTH = 270;
type RatesProps = {
  value: AccommodationRate;
  isLowestPrice: boolean;
  expandedDetails: boolean;
  setExpandedDetails: Dispatch<SetStateAction<boolean>>;
};
export function Rates(t0) {
  const $ = _c(48);
  const {
    value,
    isLowestPrice,
    expandedDetails,
    setExpandedDetails
  } = t0;
  const {
    pricing,
    cancelationDeadline,
    mealPlan,
    details
  } = value;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: RATES_WIDTH
    };
    t2 = shopTestId("Rates.rate");
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isLowestPrice) {
    t3 = isLowestPrice ? hotelAdsCriticalPath(HOTEL_ADS_NAV_ORDER.selectRate) : {};
    $[2] = isLowestPrice;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = pricing.tags?.[0];
  let t5;
  if ($[4] !== isLowestPrice || $[5] !== t4) {
    t5 = <AccommodationFlag isLowestPrice={isLowestPrice} dealTag={t4} />;
    $[4] = isLowestPrice;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== pricing.currency || $[8] !== pricing.discountPercentage || $[9] !== pricing.originalPrice) {
    t6 = pricing.discountPercentage && pricing.originalPrice && <AccommodationDiscount price={pricing.originalPrice} currency={pricing.currency} discountPercentage={pricing.discountPercentage} />;
    $[7] = pricing.currency;
    $[8] = pricing.discountPercentage;
    $[9] = pricing.originalPrice;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== pricing.currency || $[12] !== pricing.price) {
    t7 = <Price amount={pricing.price} currency={pricing.currency} className="font-heading3" />;
    $[11] = pricing.currency;
    $[12] = pricing.price;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== value.occupancy) {
    t8 = value.occupancy && <div className="font-body-small">{value.occupancy.total === 1 ? "F\xFCr 1 Person" : `Für ${formatGermanNumber(value.occupancy.total)} Personen`}</div>;
    $[14] = value.occupancy;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== value.title) {
    t9 = value.title && <div className="font-body-small font-semibold">{value.title}</div>;
    $[16] = value.title;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = <div>{t5}{t6}{t7}{t8}{t9}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="hidden size-6 shrink-0 items-center justify-center rounded-full border-2 border-primary group-aria-checked/radio:inline-flex"><Circle className="hidden size-4 fill-primary group-aria-checked/radio:block" weight="fill" /></span>;
    t12 = <span className="size-6 shrink-0 items-center justify-center rounded-full border-2 border-primary group-aria-checked/radio:hidden" />;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10) {
    t13 = <div className="flex items-start justify-between">{t10}{t11}{t12}</div>;
    $[26] = t10;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = cancelationDeadline?.local;
  const t15 = cancelationDeadline?.timezone;
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = <CancelationPolicy cancelationDeadline={t14} cancelationTimezone={t15} />;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== mealPlan) {
    t17 = mealPlan && <MealPlan mealPlan={mealPlan} />;
    $[31] = mealPlan;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== details || $[34] !== expandedDetails || $[35] !== setExpandedDetails) {
    t18 = details && <li><TextExpander readLessLabel="Weniger anzeigen" readMoreLabel="Mehr anzeigen" isExpanded={expandedDetails} onExpand={setExpandedDetails}>{details.map(_temp)}</TextExpander></li>;
    $[33] = details;
    $[34] = expandedDetails;
    $[35] = setExpandedDetails;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t16 || $[38] !== t17 || $[39] !== t18) {
    t19 = <ul className="grid gap-2">{t16}{t17}{t18}</ul>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t13 || $[42] !== t19) {
    t20 = <div className="grid gap-4 p-4 pb-6">{t13}{t19}</div>;
    $[41] = t13;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20 || $[45] !== t3 || $[46] !== value) {
    t21 = <Radio value={value} className="group/radio flex h-full cursor-pointer items-start rounded-xl border border-primary/15 p-px transition-colors hover:bg-hover-light active:bg-active-light aria-checked:border-2 aria-checked:border-primary aria-checked:bg-primary/5 aria-checked:p-0" style={t1} data-testid={t2} {...t3}>{t20}</Radio>;
    $[44] = t20;
    $[45] = t3;
    $[46] = value;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
}
function _temp(detail) {
  return <LineItem key={detail}>{detail}</LineItem>;
}